import { MarketingHeader } from '../../components/marketing/MarketingHeader';
import { MarketingFooter } from '../../components/marketing/MarketingFooter';
import { SEOHead } from '../../components/SEO/SEOHead';
import { StructuredData } from '../../components/SEO/StructuredData';
import { createBreadcrumbListSchema } from '../../components/SEO/StructuredData';

export const PrivacyPage = () => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://simcarlo.com';

  return (
    <div className="marketing-page">
      <SEOHead
        metadata={{
          title: 'Privacy Policy - Your Data Protection',
          description: 'Simcarlo\'s Privacy Policy explains how we collect, use, and protect your personal information. Learn about your privacy rights and data security.',
          keywords: ['privacy policy', 'data protection', 'GDPR', 'privacy rights', 'data security'],
          ogImage: `${baseUrl}/images/Landing Main.png`,
          canonicalUrl: `${baseUrl}/privacy`,
        }}
      />
      <StructuredData
        schemas={[
          createBreadcrumbListSchema([
            { name: 'Home', url: baseUrl },
            { name: 'Privacy Policy', url: `${baseUrl}/privacy` },
          ]),
        ]}
      />
      <MarketingHeader />

      {/* Hero Section */}
      <section className="marketing-hero">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h1 className="marketing-hero-title" style={{ textAlign: 'left', marginBottom: '16px' }}>Privacy Policy</h1>
            <p className="marketing-hero-subtitle" style={{ textAlign: 'left', marginBottom: '24px' }}>
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <p className="marketing-hero-description" style={{ textAlign: 'left' }}>
              We are committed to protecting your privacy. This policy explains how we collect, use, and safeguard your information.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="marketing-problem">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>Introduction</h2>
            
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  Simcarlo ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains what personal information we collect,
                  how we use and share it, and the choices you have. By using Simcarlo, you agree to this Privacy Policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Information We Collect Section */}
      <section className="marketing-solution">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>Information We Collect</h2>
            
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Account Information</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  When you create an account, we collect your email address, password (hashed), and profile details you provide (e.g., name).
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Usage Data</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We collect information about how you use Simcarlo, including the simulations you create, versions you save, features you use,
                  error events, and timestamps. We may collect aggregated statistics about model performance (e.g., run duration, trial counts) without
                  inspecting your proprietary data unless you explicitly share it with support.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Device and Technical Data</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We automatically receive technical data such as IP address, browser type, operating system, referring URLs, pages viewed, and device identifiers.
                  This helps us maintain security, prevent fraud, and improve performance.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Cookies and Similar Technologies</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '16px' }}>
                  We use cookies and similar technologies to authenticate users, remember preferences, and analyze usage.
                  You can control cookies through your browser settings, but disabling them may limit functionality.
                </p>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We use a consent management system that allows you to control which cookies and tracking technologies are used.
                  You can accept all, reject all, or customize your preferences at any time through the consent banner.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Payment Information</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  Payments are processed by Stripe. We receive billing metadata (e.g., name, subscription status, plan, last 4 digits of card)
                  but we do not store full credit card numbers or sensitive payment data on our servers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Use Information Section */}
      <section className="marketing-problem">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>How We Use Your Information</h2>
            
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '24px' }}>
                  We use the information we collect to:
                </p>
                <ul className="marketing-feature-list" style={{ textAlign: 'left', maxWidth: '100%', margin: '0' }}>
                  <li><span className="marketing-checkmark">✓</span> Provide, maintain, and improve our service</li>
                  <li><span className="marketing-checkmark">✓</span> Process transactions and send related information</li>
                  <li><span className="marketing-checkmark">✓</span> Send you technical notices and support messages</li>
                  <li><span className="marketing-checkmark">✓</span> Respond to your comments and questions</li>
                  <li><span className="marketing-checkmark">✓</span> Monitor and analyze usage patterns and protect against fraud and abuse</li>
                  <li><span className="marketing-checkmark">✓</span> Develop new features, including AI-assisted modeling tools</li>
                  <li><span className="marketing-checkmark">✓</span> Comply with legal obligations and enforce our terms</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Bases Section */}
      <section className="marketing-solution">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>Legal Bases for Processing (EEA/UK)</h2>
            
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '24px' }}>
                  If you are in the EEA or UK, we process your information under the following legal bases:
                </p>
                <ul className="marketing-feature-list" style={{ textAlign: 'left', maxWidth: '100%', margin: '0' }}>
                  <li><span className="marketing-checkmark">✓</span> Contract: to provide Simcarlo per our Terms of Service</li>
                  <li><span className="marketing-checkmark">✓</span> Legitimate interests: to secure and improve our services</li>
                  <li><span className="marketing-checkmark">✓</span> Consent: for optional cookies or communications where required</li>
                  <li><span className="marketing-checkmark">✓</span> Legal obligation: to comply with applicable laws</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Share Section */}
      <section className="marketing-problem">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>How We Share Information</h2>
            
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '24px' }}>
                  We do not sell your personal information. We share data with:
                </p>
                <ul className="marketing-feature-list" style={{ textAlign: 'left', maxWidth: '100%', margin: '0' }}>
                  <li><span className="marketing-checkmark">✓</span> Service providers (e.g., hosting, databases, analytics, payments) who act as processors</li>
                  <li><span className="marketing-checkmark">✓</span> Professional advisors (e.g., legal, accounting) under confidentiality</li>
                  <li><span className="marketing-checkmark">✓</span> Authorities when required by law or to protect rights and safety</li>
                  <li><span className="marketing-checkmark">✓</span> In connection with a merger, acquisition, or asset sale (with notice where required)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Sections */}
      <section className="marketing-solution">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>International Data Transfers</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We may transfer your information to countries outside your own. Where required, we rely on appropriate safeguards
                  such as Standard Contractual Clauses (SCCs) for cross-border transfers.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Data Security</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We implement appropriate technical and organizational measures to protect your personal information (e.g., encryption at rest/in transit,
                  least-privilege access, audit logging). However, no method of transmission over the Internet or electronic storage is 100% secure.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Data Retention</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We retain information for as long as your account is active or as needed to provide Simcarlo and for legitimate business needs
                  (e.g., security, analytics, legal compliance). When no longer needed, we delete or anonymize data in accordance with our retention policies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Rights Section */}
      <section className="marketing-problem">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>Your Rights</h2>
            
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '24px' }}>
                  Depending on your location, you may have the right to:
                </p>
                <ul className="marketing-feature-list" style={{ textAlign: 'left', maxWidth: '100%', margin: '0 0 24px 0' }}>
                  <li><span className="marketing-checkmark">✓</span> Access, correct, or delete your personal information</li>
                  <li><span className="marketing-checkmark">✓</span> Object to or restrict certain processing</li>
                  <li><span className="marketing-checkmark">✓</span> Data portability</li>
                  <li><span className="marketing-checkmark">✓</span> Withdraw consent where processing is based on consent</li>
                  <li><span className="marketing-checkmark">✓</span> Lodge a complaint with your local data protection authority</li>
                </ul>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  To exercise rights, contact us at <a href="mailto:privacy@simcarlo.com" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>privacy@simcarlo.com</a>.
                  We may need to verify your identity before responding.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Sections */}
      <section className="marketing-solution">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Children's Privacy</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  Simcarlo is not directed to children under 13, and we do not knowingly collect personal information from children.
                  If you believe a child has provided us information, please contact us to request deletion.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Do Not Track</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  Some browsers offer a "Do Not Track" (DNT) signal. Because there is no industry standard for DNT, we do not respond to these signals.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Analytics and Tracking Services</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '16px' }}>
                  We use the following analytics and tracking services to understand how visitors interact with our website and improve our services.
                  These services are only enabled with your explicit consent, which you can manage through our consent banner:
                </p>
                <ul style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', paddingLeft: '24px', marginBottom: '16px' }}>
                  <li>
                    <strong>Google Analytics 4 (GA4)</strong> - We use Google Analytics to collect and analyze information about how visitors use our website.
                    This includes page views, user interactions, and aggregated usage statistics. Google Analytics uses cookies and may collect information such as
                    your IP address, browser type, device information, and pages visited. Google's use of information is governed by their{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>Privacy Policy</a> and{' '}
                    <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>Data Processing Terms</a>.
                    You can opt out of Google Analytics by using the{' '}
                    <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>Google Analytics Opt-out Browser Add-on</a>.
                  </li>
                  <li style={{ marginTop: '12px' }}>
                    <strong>Microsoft Clarity</strong> - We use Microsoft Clarity to record user sessions, create heatmaps, and analyze user behavior on our website.
                    Clarity helps us understand how users navigate and interact with our interface to improve usability. Clarity may collect information such as
                    mouse movements, clicks, scrolls, and page interactions. Microsoft's use of information is governed by their{' '}
                    <a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>Privacy Statement</a>.
                    You can learn more about Clarity at{' '}
                    <a href="https://clarity.microsoft.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>clarity.microsoft.com</a>.
                  </li>
                </ul>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  Both services are configured to respect your privacy preferences and will only collect data if you have consented to analytics cookies.
                  You can change your consent preferences at any time through the consent banner that appears on our website.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Third-Party Services and Subprocessors</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '16px' }}>
                  We use trusted service providers to operate Simcarlo. These include, for example:
                </p>
                <ul style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', paddingLeft: '24px' }}>
                  <li>Supabase (authentication, database, storage)</li>
                  <li>Stripe (payments and billing)</li>
                  <li>Google Analytics 4 (analytics and usage tracking - see above)</li>
                  <li>Microsoft Clarity (session recording and heatmaps - see above)</li>
                  <li>Cloud hosting and content delivery providers</li>
                </ul>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', marginTop: '16px' }}>
                  We require our processors to protect your data and only process it per our instructions.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Changes to This Policy</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We may update this Privacy Policy from time to time. We will notify you by updating the "Last updated" date,
                  and for material changes, we may provide additional notice (e.g., email or in-app).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="marketing-problem">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>Contact Us</h2>
            
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  If you have questions about this Privacy Policy or wish to exercise your rights, contact{' '}
                  <a href="mailto:privacy@simcarlo.com" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>privacy@simcarlo.com</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};
